import { useTypedSelector } from "@/config/configureAppStore";
import { IClientProjectData } from "@/features/clientProjectDetails";
import { useCallback, useMemo } from "react";
import { RATES } from "../constants";
import { convertArrayToMap } from "@/utils/json";

const useProjectCredits = ({
  projectId,
  isCreatingProject = false
}: {
  projectId: string;
  isCreatingProject?: boolean;
}) => {
  const projectDetails = useTypedSelector(
    (state) => state.currentClientProject[projectId]?.project
  );
  const unsavedChanges = useTypedSelector((state) => state.clientProjectPopup);

  const { nonQCTargetLocales, qcTargetLocales } = useTypedSelector((state) => ({
    nonQCTargetLocales:
      state.user.activeWorkspaceData?.supportedNonQCTargetLocales || [],
    qcTargetLocales:
      state.user.activeWorkspaceData?.supportedQCTargetLocales || []
  }));
  const nonQCTargetLocalesMap = convertArrayToMap(nonQCTargetLocales, "locale");
  const qcTargetLocalesMap = convertArrayToMap(qcTargetLocales, "locale");

  const calculateCreditsRate = useCallback(
    (projectDetails: IClientProjectData): number => {
      if (!projectDetails) {
        return 0;
      }
      let calc = RATES.PRE_PROCESSING;

      if (projectDetails?.targetLocales) {
        const qcCost = projectDetails.qualityCheckEnabled ? RATES.QC_COST : 0;
        const lipSyncCost = projectDetails.lipSyncEnabled
          ? RATES.LIP_SYNC_COST
          : 0;
        calc +=
          projectDetails.targetLocales.length *
          (RATES.FIXED_DUB_COST + qcCost + lipSyncCost);

        if (projectDetails.lipSyncEnabled) {
          calc += RATES.FACE_MARKING;
        }

        // Backend is sending the extra credits for QC and non QC locales
        projectDetails.targetLocales.forEach((locale) => {
          if (projectDetails.qualityCheckEnabled) {
            calc += qcTargetLocalesMap[locale]?.credit || 0;
          } else {
            calc += nonQCTargetLocalesMap[locale]?.credit || 0;
          }
        });
      }

      return calc;
    },
    [nonQCTargetLocalesMap, qcTargetLocalesMap]
  );

  const creditsRate = useMemo(() => {
    if (
      projectId &&
      (projectDetails || unsavedChanges.projectId === projectId)
    ) {
      let payload = {
        ...projectDetails
      };

      if (isCreatingProject) {
        payload = {
          ...payload,
          ...unsavedChanges
        };
      }

      return calculateCreditsRate({
        ...payload,
        targetLocales: [
          ...(projectDetails?.targetLocales || []),
          ...(unsavedChanges.targetLocales || [])
        ]
      });
    }
    return 0;
  }, [
    projectId,
    projectDetails,
    unsavedChanges,
    isCreatingProject,
    calculateCreditsRate
  ]);

  return {
    creditsRate: creditsRate
  };
};

export default useProjectCredits;
